import * as reduxApi from 'src/utils/redux-api-bff';

import type {Dispatch, ThunkAction} from 'src/reducers';
import type {
  WritebackFieldValue,
  FieldWritebackChangeToOperatorType,
} from 'src/types/field-writeback';
import type {
  WritebackFieldProperty,
  ObjectWritebackFieldMeta,
  WritebackEntityType,
} from 'src/types/object-writeback';
import type {ObjectWritebackState} from 'src/reducers/object-writeback';
import type {NodeSchedule} from 'src/types/node-schedule';

import {thunkify as flow} from 'src/utils/thunks';
import {key, fetching} from 'src/utils/redux';
import logger from 'src/utils/logger';


type UpdateObjectWritebackPayload = Partial<ObjectWritebackState>;

type ObjectWritebackCriteriaType = 'field' | 'person';
export const RECEIVE_WRITEBACK_ENTITY_TYPES =
  'object-writeback/receive-entity-types';

export const SET_OBJECT_WRITEBACK_ENTITY_ID = 'object-writeback/set-entity-id';
export const ADD_FIELDS_TO_WRITEBACK_FIELD_MAPPING =
  'object-writeback/add-fields-to-writeback-field-mapping';

export const REMOVE_OBJECT_WRITEBACK_FIELD = 'object-writeback/remove-field';
export const REMOVE_FIELDS_FROM_WRITEBACK_FIELD_MAPPING =
  'object-writeback/remove-fields-from-writeback-field-mapping';
export const SET_BULK_FIELD_PROPERTY =
  'object-writeback/set-bulk-field-property';

export const RECEIVE_OBJECT_WRITEBACK_INFO = 'object-writeback/receive-info';

export const SET_OBJECT_WRITEBACK = 'object-writeback/set';
export const UPDATE_OBJECT_WRITEBACK = 'object-writeback/update';
export const UPDATE_NODE_SCHEDULE = 'object-writeback/update-node-schedule';
export const RESET_OBJECT_WRITEBACK = 'object-writeback/reset';
export const SET_OBJECT_WRITEBACK_CHANGE_TYPE =
  'object-writeback/set-criteria-type';
export const SET_OBJECT_WRITEBACK_FIELD =
  'object-writeback/set-writeback-field';
export const SET_OBJECT_WRITEBACK_VALUE =
  'object-writeback/set-writeback-value';
export const SET_OBJECT_WRITEBACK_CHANGE_TO_OPERATOR =
  'object-writeback/set-change-to-operator';

export const UPDATE_OBJECT_WRITEBACK_FIELD = 'object-writeback/update-field';

export const INITIALIZE_WRITEBACK_FIELDS = 'object-writeback/initialize-fields';
export const SET_WRITEBACK_FIELD_META = 'object-writeback/set-field-meta';

type ReceiveWritebackEntityTypesAction = {
  type: 'object-writeback/receive-entity-types';
  payload: Array<WritebackEntityType>;
};

type RecieveObjectWritebackInfoAction = {
  type: typeof RECEIVE_OBJECT_WRITEBACK_INFO;
  payload: ObjectWritebackState;
};
type UpdateObjectWritebackAction = {
  type: 'object-writeback/update';
  payload: UpdateObjectWritebackPayload;
};
type UpdateNodeScheduleAction = {
  type: typeof UPDATE_NODE_SCHEDULE;
  payload: Partial<NodeSchedule>;
};

type ResetObjectWritebackAction = {
  type: 'object-writeback/reset';
  payload?: ObjectWritebackState;
};
type SetBulkFieldPropertyAction = {
  type: 'object-writeback/set-bulk-field-property';
  payload: {
    [key: string]: WritebackFieldProperty;
  };
};
type SetObjectWritebackEntityIdAction = {
  type: 'object-writeback/set-entity-id';
  payload: string;
};
type SetWritebackFieldMetaAction = {
  type: 'object-writeback/set-field-meta';
  payload: Array<ObjectWritebackFieldMeta>;
};
type SetObjectWritebackAction = {
  type: 'object-writeback/set';
  payload: ObjectWritebackState;
};
type SetObjectWritebackCriteriaTypeAction = {
  type: 'object-writeback/set-criteria-type';
  payload: ObjectWritebackCriteriaType;
};
type SetObjectWritebackFieldsAction = {
  type: 'object-writeback/set-writeback-field';
  payload: WritebackFieldValue;
};
type SetObjectWritebackValueAction = {
  type: 'object-writeback/set-writeback-value';
  payload: {
    id: string;
    value: Array<string>;
  };
};
type SetObjectWritebackChangeToOperatorAction = {
  type: 'object-writeback/set-change-to-operator';
  payload: {
    id: string;
    value: FieldWritebackChangeToOperatorType;
  };
};

type SetObjectWritebackMetaMap = {
  type: 'object-writeback/set-field-wb-metamap';
  payload: {
    [key: string]: string | null | undefined;
  };
};

type AddFieldsToWritebackFieldMappingAction = {
  type: typeof ADD_FIELDS_TO_WRITEBACK_FIELD_MAPPING;
  payload: {
    [key: string]: WritebackFieldProperty;
  };
};

type UpdateFieldPropertyAction = {
  type: typeof UPDATE_OBJECT_WRITEBACK_FIELD;
  payload: {
    fieldId: string;
    updates: WritebackFieldProperty;
  };
};

type RemoveFieldsFromWritebackFieldMappingAction = {
  type: typeof REMOVE_FIELDS_FROM_WRITEBACK_FIELD_MAPPING;
  payload: string;
};

type InitializeWritebackFieldsAction = {
  type: typeof INITIALIZE_WRITEBACK_FIELDS;
  payload: {
    allFields: Array<ObjectWritebackFieldMeta>;
    requiredFieldMapping: {
      [key: string]: WritebackFieldProperty;
    };
  };
};

export type ObjectWritebackAction =
  | ReceiveWritebackEntityTypesAction
  | AddFieldsToWritebackFieldMappingAction
  | RemoveFieldsFromWritebackFieldMappingAction
  | UpdateFieldPropertyAction
  | UpdateObjectWritebackAction
  | UpdateNodeScheduleAction
  | ResetObjectWritebackAction
  | SetObjectWritebackAction
  | SetObjectWritebackCriteriaTypeAction
  | SetObjectWritebackChangeToOperatorAction
  | SetObjectWritebackMetaMap
  | SetBulkFieldPropertyAction
  | RecieveObjectWritebackInfoAction
  | SetObjectWritebackEntityIdAction;

export const receiveWritebackEntityTypes = (
  payload: Array<WritebackEntityType>,
): ReceiveWritebackEntityTypesAction => ({
  type: RECEIVE_WRITEBACK_ENTITY_TYPES,
  payload,
});

export const receiveObjectWriteback = (
  payload: ObjectWritebackState,
): RecieveObjectWritebackInfoAction => ({
  type: RECEIVE_OBJECT_WRITEBACK_INFO,
  payload,
});
export const updateObjectWriteback = (
  payload: UpdateObjectWritebackPayload,
): UpdateObjectWritebackAction => ({
  type: UPDATE_OBJECT_WRITEBACK,
  payload,
});

export const updateNodeSchedule = (
  payload: Partial<NodeSchedule>,
): UpdateNodeScheduleAction => ({
  type: UPDATE_NODE_SCHEDULE,
  payload,
});
export const resetObjectWriteback = (): ResetObjectWritebackAction => ({
  type: RESET_OBJECT_WRITEBACK,
});

export const setObjectWriteback = (
  payload: ObjectWritebackState,
): SetObjectWritebackAction => ({
  type: SET_OBJECT_WRITEBACK,
  payload,
});
export const setObjectWritebackCriteriaType = (
  payload: ObjectWritebackCriteriaType,
): SetObjectWritebackCriteriaTypeAction => ({
  type: SET_OBJECT_WRITEBACK_CHANGE_TYPE,
  payload,
});
export const setObjectWritebackField = (
  payload: WritebackFieldValue,
): SetObjectWritebackFieldsAction => ({
  type: SET_OBJECT_WRITEBACK_FIELD,
  payload,
});
export const setObjectWritebackValue = (payload: {
  id: string;
  value: Array<string>;
}): SetObjectWritebackValueAction => ({
  type: SET_OBJECT_WRITEBACK_VALUE,
  payload,
});
export const setObjectWritebackChangeToOperator = (payload: {
  id: string;
  value: FieldWritebackChangeToOperatorType;
}): SetObjectWritebackChangeToOperatorAction => ({
  type: SET_OBJECT_WRITEBACK_CHANGE_TO_OPERATOR,
  payload,
});

export const getWritebackEntityTypes: () => ThunkAction<unknown> = flow(
  key(() => 'writeback-entity-types'),
  fetching(),
)(() => (dispatch: Dispatch) => {
  return dispatch(reduxApi.get('writeback/owb/entity-types'))
    .then((payload) => {
      dispatch(receiveWritebackEntityTypes(payload));
    })
    .catch((error) => {
      logger.error('error', error);
    });
});

export const getFieldWriteback: (
  id: string,
  swid: number,
) => ThunkAction<unknown> | unknown =
  (id, swid) => (dispatch: Dispatch, getState: any) => {
    //we already have data for fwb in the swimlane data so we pick it from there instead of getting it from the api
    // we can also hit the api from here to get the data if we want
    const fieldWritebackData = getState().automationWorkflow.swimlanes.find(
      // @ts-expect-error - TS7006 - Parameter 'swimlane' implicitly has an 'any' type.
      (swimlane) => swimlane.id === swid,
    ).nodes[id];
    return fieldWritebackData ?? null;
  };

export const setObjectWritebackEntityId = (
  payload: string,
): SetObjectWritebackEntityIdAction => ({
  type: SET_OBJECT_WRITEBACK_ENTITY_ID,
  payload,
});

export const initializeWritebackFields = (
  allFields: Array<ObjectWritebackFieldMeta>,
  requiredFieldMapping: {
    [key: string]: WritebackFieldProperty;
  },
): InitializeWritebackFieldsAction => ({
  type: INITIALIZE_WRITEBACK_FIELDS,
  payload: {allFields, requiredFieldMapping},
});

export const setWritebackFieldMeta = (
  allFields: Array<ObjectWritebackFieldMeta>,
): SetWritebackFieldMetaAction => ({
  type: SET_WRITEBACK_FIELD_META,
  payload: allFields,
});

/* Actions that are being Used in Object Writeback*/

export const getWritebackFieldMetaAndInitialize: (
  objectId: string,
) => ThunkAction<void> = flow(
  key((objectId: string) => `writeback-field-meta-${objectId}`),
  fetching(),
)((objectId: string) => async (dispatch: Dispatch) => {
  try {
    const payload = await dispatch(
      reduxApi.get(`writeback/owb/object-id/${objectId}/j2-field-meta`),
    );
    const allFields = payload?.fields ?? [];
    const requiredFields = allFields.filter(
      // @ts-expect-error - TS7006 - Parameter 'field' implicitly has an 'any' type.
      (field) => field.createOperation === 'REQUIRED',
    );
    const requiredFieldsMapping = getFieldsMapping(requiredFields);
    // @ts-expect-error - TS2769 - No overload matches this call.
    dispatch(initializeWritebackFields(allFields, requiredFieldsMapping));
  } catch (error: any) {
    logger.error('error', error);
    throw error;
  }
});

export const getWritebackFieldMeta: (objectId: string) => ThunkAction<unknown> =
  flow(
    key((objectId: string) => `writeback-field-meta-${objectId}`),
    fetching(),
  )((objectId) => (dispatch: Dispatch) => {
    return dispatch(
      reduxApi.get(`writeback/owb/object-id/${objectId}/j2-field-meta`),
    )
      .then((payload) => {
        const allFields = payload?.fields ?? [];
        return allFields;
      })
      .catch((error) => {
        logger.error('error', error);
      });
  });

export const getFieldsMapping = (
  fields: Array<ObjectWritebackFieldMeta>,
): {
  [key: string]: WritebackFieldProperty;
} => {
  const fieldsMapping: Record<string, any> = {};
  fields.forEach((field) => {
    fieldsMapping[field.id] = {
      id: field.id,
      changeType: '',
      value: [],
      isValidChangeType: true,
      isValidValue: true,
      parentId: field.parentFieldId,
    };
  });
  return fieldsMapping;
};

export const addFieldsToWritebackFieldMapping = (
  fields: Array<ObjectWritebackFieldMeta>,
): AddFieldsToWritebackFieldMappingAction => ({
  type: ADD_FIELDS_TO_WRITEBACK_FIELD_MAPPING,
  payload: getFieldsMapping(fields),
});

export const removeFieldFromWritebackFieldMapping = (
  fieldId: string,
): RemoveFieldsFromWritebackFieldMappingAction => ({
  type: REMOVE_FIELDS_FROM_WRITEBACK_FIELD_MAPPING,
  payload: fieldId,
});

/**
 * Update a field property in the writeback field mapping
 * @param fieldId - The ID of the field to update
 * @param updates - Object containing the properties to update
 * @returns Thunk action to dispatch the updateWritebackFieldMapping action
 */

export const updateFieldProperty = (
  fieldId: string,
  updates: Partial<WritebackFieldProperty>,
): UpdateFieldPropertyAction => ({
  type: UPDATE_OBJECT_WRITEBACK_FIELD,
  payload: {
    fieldId,
    // @ts-expect-error - TS2322 - Type 'Partial<WritebackFieldProperty>' is not assignable to type 'WritebackFieldProperty'.
    updates,
  },
});

export const setBulkFieldProperty = (payload: {
  [key: string]: Partial<WritebackFieldProperty>;
}): SetBulkFieldPropertyAction => ({
  type: SET_BULK_FIELD_PROPERTY,
  // @ts-expect-error - TS2322 - Type '{ [key: string]: Partial<WritebackFieldProperty>; }' is not assignable to type '{ [key: string]: WritebackFieldProperty; }'.
  payload,
});
